import React, { useCallback, useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/AuthContext";
import {
  House, FileText, CalendarBlank, Buildings, Users as UsersIcon,
  ShieldCheck, SignOut, X, Factory, Scales, ShieldStar, BookOpen, ClockClockwise, Sun, Moon, Question, ChartLineUp, TreeStructure,
  Graph
} from "@phosphor-icons/react";
import NotificationsBell from "./NotificationsBell";
import CompanySwitcher from "./CompanySwitcher";
import HeaderContext, { useOrgName } from "./structure/HeaderContext";
import StructurePanel from "./structure/StructurePanel";
import { canUseStructures, prefetchStructures } from "@/hooks/useStructureDetail";
import { useTheme } from "@/ThemeContext";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Administrador",
  editor: "Editor",
  viewer: "Visualizador",
};

const NAV = [
  { to: "/", label: "Dashboard", icon: House, module: "dashboard" },
  { to: "/instalaciones", label: "Instalaciones", icon: Factory, module: "installations" },
  { to: "/obligaciones", label: "Obligaciones", icon: Scales, module: "obligations" },
  { to: "/documentos", label: "Documentos", icon: FileText, module: "documents" },
  { to: "/cumplimiento", label: "Cumplimiento", icon: ShieldStar, module: "compliance" },
  { to: "/reportes/tarjeta-informativa", label: "Tarjeta Informativa", icon: ChartLineUp, module: "compliance" },
  { to: "/calendario", label: "Calendario", icon: CalendarBlank, module: "calendar" },
  { to: "/perfil-empresa", label: "Perfil de Empresa", icon: Buildings, module: "company_profile" },
  { to: "/usuarios", label: "Usuarios", icon: UsersIcon, module: "users" },
  { to: "/auditoria", label: "Auditoría", icon: ClockClockwise, module: "audit" },
  { to: "/catalogos", label: "Catálogos", icon: BookOpen, module: "catalogs" },
  { to: "/super-admin", label: "Super Admin", icon: ShieldCheck, module: "super_admin" },
  { to: "/organizations", label: "Estructuras Org.", icon: TreeStructure, module: "super_admin" },
  { to: "/manual", label: "Manual", icon: Question, module: "__public__" },
];

const PIN_KEY = "zae-structure-panel-pinned";

const SidebarStructure = ({ onOpen }) => {
  const { structures, activeStructureId } = useAuth();
  const s = (structures || []).find(x => x.id === activeStructureId);
  return (
    <div className="mb-3 pb-3 border-b border-neutral-200 space-y-1" data-testid="sidebar-structure-section">
      <div className="px-3 pt-1 pb-1 text-[10px] font-bold uppercase tracking-widest text-neutral-400">Estructura organizacional</div>
      {s && (
        <button onClick={onOpen} className="w-full text-left ml-0 px-3 py-2 rounded-md text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 truncate" data-testid="sidebar-active-structure">
          {s.name}
        </button>
      )}
      <button onClick={onOpen} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors" data-testid="sidebar-navigate-structure-btn">
        <Graph size={18} weight="duotone"/> Navegar estructura
      </button>
    </div>
  );
};

const HeaderControls = () => {
  const { theme, toggle: toggleTheme } = useTheme();
  const { contextVersion } = useAuth();
  return (
    <div className="flex items-center gap-2 shrink-0">
      <CompanySwitcher/>
      <button
        onClick={toggleTheme}
        className="p-2 rounded-md text-neutral-500 hover:text-neutral-900 hover:bg-neutral-100 transition-colors"
        aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
        title={theme === "dark" ? "Modo claro" : "Modo oscuro"}
        data-testid="theme-toggle-btn"
      >
        {theme === "dark" ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
      </button>
      <NotificationsBell key={contextVersion}/>
    </div>
  );
};

export default function Layout() {
  const { user, logout, can, structures, contextVersion } = useAuth();
  const nav = useNavigate();
  const orgName = useOrgName();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(() => localStorage.getItem(PIN_KEY) === "1");
  const [panelOpen, setPanelOpen] = useState(() => localStorage.getItem(PIN_KEY) === "1");
  const closePanel = useCallback(() => {
    setPanelOpen(false); setPinned(false); localStorage.removeItem(PIN_KEY);
  }, []);
  const togglePin = () => setPinned(p => { const v = !p; v ? localStorage.setItem(PIN_KEY, "1") : localStorage.removeItem(PIN_KEY); return v; });

  useEffect(() => {
    if (canUseStructures(user, structures)) prefetchStructures(structures.map(s => s.id));
  }, [user?.id, structures]); // eslint-disable-line

  if (!user) return null;
  const links = NAV.filter(n => n.module === "__public__" || can(n.module, "read"));
  const structuresOn = canUseStructures(user, structures);
  const showPanel = structuresOn && panelOpen;
  const handleLogout = () => { logout(); nav("/login"); };
  const openPanel = () => { setOpen(false); setPanelOpen(true); };

  return (
    <div className="min-h-screen flex flex-col bg-neutral-50">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-neutral-200 px-4 lg:px-6 h-16 flex items-center justify-between gap-4" data-testid="app-header">
        <HeaderContext onOpenPanel={openPanel} onOpenSidebar={() => setOpen(true)}/>
        <HeaderControls/>
      </header>

      <div className="flex flex-1 min-h-0">
        <aside className={`${open ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:sticky top-0 lg:top-16 left-0 z-40 w-64 h-screen lg:h-[calc(100vh-4rem)] bg-white border-r border-neutral-200 flex flex-col transition-transform`}
               data-testid="sidebar">
          <div className="px-5 py-3 border-b border-neutral-200 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Menú</span>
            <button onClick={()=>setOpen(false)} className="lg:hidden text-neutral-500" data-testid="sidebar-close-btn">
              <X size={20} />
            </button>
          </div>
          <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
            {structuresOn && <SidebarStructure onOpen={openPanel}/>}
            {links.map((n) => (
              <NavLink
                key={n.to} to={n.to} end={n.to === "/"}
                onClick={()=>setOpen(false)}
                data-testid={`nav-${n.to.replace(/\//g,'') || 'home'}-link`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-200
                  ${isActive ? "bg-blue-50 text-blue-700 border border-blue-100" : "text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"}`}
              >
                <n.icon size={18} weight="duotone" />
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="p-3 border-t border-neutral-200">
            <div className="flex items-center gap-3 p-2.5 rounded-md">
              <div className="w-9 h-9 bg-gradient-to-br from-rose-500 to-amber-500 rounded-md flex items-center justify-center text-white font-bold">
                {user.name?.[0]?.toUpperCase() || "U"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate text-neutral-900" data-testid="user-name-display">{user.name}</div>
                <div className="text-xs text-neutral-500">{ROLE_LABEL[user.role]}</div>
              </div>
              <button onClick={handleLogout} className="text-neutral-400 hover:text-rose-500 transition-colors" data-testid="logout-btn" title="Salir">
                <SignOut size={18} />
              </button>
            </div>
          </div>
        </aside>

        {open && <div onClick={()=>setOpen(false)} className="lg:hidden fixed inset-0 bg-black/40 z-30"></div>}

        {showPanel && pinned && (
          <aside className="hidden lg:block sticky top-16 h-[calc(100vh-4rem)] w-[380px] shrink-0 border-r border-neutral-200 anim-fade-up" data-testid="structure-panel-pinned">
            <StructurePanel orgName={orgName} pinned onTogglePin={togglePin} onClose={closePanel}/>
          </aside>
        )}

        <main className="flex-1 min-w-0 flex flex-col">
          <div className="flex-1 p-6 lg:p-10 overflow-x-hidden">
            <Outlet key={contextVersion} />
          </div>
        </main>
      </div>

      {showPanel && (
        <div className={`fixed inset-0 z-50 ${pinned ? "lg:hidden" : ""}`} data-testid="structure-panel-overlay">
          <div className="absolute inset-0 bg-neutral-900/50 backdrop-blur-[1px]" onClick={closePanel} data-testid="structure-panel-backdrop"/>
          <aside className="absolute left-0 top-0 h-full w-[400px] max-w-[92vw] shadow-2xl anim-slide-in" data-testid="structure-panel">
            <StructurePanel orgName={orgName} pinned={pinned} onTogglePin={togglePin} onClose={closePanel}/>
          </aside>
        </div>
      )}
    </div>
  );
}
